import { call, put, takeEvery, select } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';

import { RootState } from '../reducers/rootReducer';
import { resumePickTourWithOrderSelection, getPickTourItems } from '../actions/activePickTour.actions';
import { setLoading } from '../actions/loading.actions';
import { setAppPage } from '../actions/appPage.actions';

import {
  BFF_V1,
  BFF_PICK_TOURS,
  PICKING_APIGEE_BASE_PATH,
  APP_PAGE_ORDER_LIST,
  GENERIC_NON_SERVICE_ERROR,
  ORDER_BEING_EDITED_ERROR,
  SAGA_RESUME_PICK_TOUR_WITH_ORDER_SELECTION,
} from '../../constants';

import { ConfigAuthValues } from '../../types';
import { callAPI } from '../../utils';
import { catchApiErrors } from './utils';

export function* runResumePickTourWithOrderSelectionSaga(action: ReturnType<typeof resumePickTourWithOrderSelection>) {
  const configAuthValues: ConfigAuthValues = yield select((state: RootState) => state.config.authValues);

  yield put(setLoading({ sagaName: SAGA_RESUME_PICK_TOUR_WITH_ORDER_SELECTION, isLoading: true }));

  try {
    const { pickTourData, pickTourType } = action.payload;

    // Step 1: Call PUT to update pick tour with filtered metadata AND change status to PROGRESS
    const updatePickTourPayload = {
      ...pickTourData,
      pickTourStatus: 'OPEN',
      pickTourSubStatus: 'PROGRESS',
      pickTourList: [], // Backend doesn't need the full item list
    };

    yield call(callAPI, {
      method: 'PUT',
      url: `${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${pickTourData.pickTourID}`,
      data: updatePickTourPayload,
    });

    // Step 2: After successfully updating picktour status to progress, fetch items for each order in the picktour
    yield put(
      getPickTourItems({
        isPickTourCreation: true,
        page: 0,
        pickTourData,
        pickTourType,
      })
    );
  } catch (error) {
    // Detect ORDER_BEING_EDITED error (HTTP 400 with specific error detail)
    const isOrderBeingEditedError =
      error?.response?.data?.errors &&
      error?.response?.status === 400 &&
      error?.response?.data?.errors?.some(
        (e: { code: string; detail: string }) =>
          e.code === GENERIC_NON_SERVICE_ERROR && e.detail === ORDER_BEING_EDITED_ERROR
      );

    // If ORDER_BEING_EDITED, navigate back to order list
    if (isOrderBeingEditedError) {
      yield put(setAppPage({ name: APP_PAGE_ORDER_LIST }));
    }

    // Standard error handling - creates toast with correct error structure
    try {
      const { pickTourID } = action.payload.pickTourData;
      const url = `${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${pickTourID}`;
      const method = 'PUT';
      const data = action.payload.pickTourData;
      const config = configAuthValues;
      yield catchApiErrors(error, { url, method, data, config, saga: SAGA_RESUME_PICK_TOUR_WITH_ORDER_SELECTION });
    } catch (_error) {
      yield catchApiErrors(error);
    }

    // For ORDER_BEING_EDITED, stop execution - don't proceed to GET items
    if (isOrderBeingEditedError) {
      return;
    }
  } finally {
    yield put(setLoading({ sagaName: SAGA_RESUME_PICK_TOUR_WITH_ORDER_SELECTION, isLoading: false }));
  }
}

export default function* watchResumePickTourWithOrderSelection() {
  yield takeEvery(getType(resumePickTourWithOrderSelection), runResumePickTourWithOrderSelectionSaga);
}
